import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./styles.scss";
import Nav from "./Nav";
import Footer from "./Footer";
import FinalCta from "./FinalCta";
import { SectionLabel } from "./ui";

import { Helmet } from "react-helmet-async";

const INDUSTRIES = [
  {
    id: "consumer-goods", code: "CG", name: "Consumer Goods", sub: "FMCG · CPG · Distribution",
    headline: "Move more cases. Collect every dollar. See every outlet.",
    intro: "We were built inside this industry. EkkBaz agents already orchestrate route-to-market, secondary sales, trade promos, credit and collections for FMCG companies across South & Southeast Asia.",
    pains: [
      { p: "Distributors go dark for weeks", a: "Distributor Management Agent surfaces silence in 24h" },
      { p: "AR drags on, working capital bleeds", a: "AR Collections Agent cuts DSO by 18–22 days" },
      { p: "Trade promos leak through the channel", a: "Salesforce Agent reconciles claims against real POS" },
      { p: "Festival demand spikes catch you flat", a: "Demand Forecast Agent pre-positions inventory weeks early" },
    ],
    case: {
      headline: "Regional FMCG distributor · SE Asia",
      pull: "Replaced a 14-person collections team. DSO −18 days in one quarter. No layoffs — they redeployed to growth roles.",
      kpi: [{ k: "DSO", v: "−18d" }, { k: "Working capital freed", v: "$7.4M" }, { k: "Ops cost", v: "−$1.9M / yr" }],
    },
    agents: ["ar-collections", "distributor", "salesforce", "demand-forecast", "financing"],
  },
  {
    id: "electronics", code: "EL", name: "Electronics", sub: "OEM · Component · Channel",
    headline: "Tame supplier risk. Compress warranty cycles. Read your channel.",
    intro: "Electronics is unforgiving — supplier shocks crater your BOM cost, warranty cycles eat your margin, and grey-channel inventory tanks your brand. EkkBaz agents instrument the whole chain.",
    pains: [
      { p: "BOM cost moves daily, suppliers play games", a: "Procurement Agent runs multi-tier supplier intel + alternatives" },
      { p: "Warranty triage drags on, customers churn", a: "CX Concierge auto-routes warranty with parts pre-staged" },
      { p: "Channel inventory mismatch creates fire sales", a: "Distributor Agent re-balances inventory across partners" },
      { p: "After-sales is a P&L drag", a: "Service Ops Agent dispatches techs with right-first-time parts" },
    ],
    case: {
      headline: "Consumer electronics OEM · 22 markets",
      pull: "Built a supplier-onboarding agent in five weeks. Now processes more suppliers per week than the previous team did per quarter — full audit trail.",
      kpi: [{ k: "Onboarding cycle", v: "21d → 38h" }, { k: "Warranty cost", v: "−38%" }, { k: "Channel visibility", v: "100%" }],
    },
    agents: ["supplier-onboarding", "cx-concierge", "distributor", "delivery"],
  },
  {
    id: "healthcare", code: "HC", name: "Healthcare", sub: "Provider · Payer · Pharma",
    headline: "Cleaner claims. Better care coordination. HIPAA from day one.",
    intro: "Denial-prevention, prior-auth and patient-flow agents — built for the regulations you actually operate under. PHI never leaves your VPC. Audit trails for every decision.",
    pains: [
      { p: "Claim denials cost millions, appeals take months", a: "Claims Agent catches denials before submission, predicts payer behavior" },
      { p: "Prior-auth is a phone-call factory", a: "Prior-Auth Agent runs end-to-end across payer portals + faxes" },
      { p: "Patient no-shows hit revenue + outcomes", a: "Scheduling Agent predicts no-shows + multilingual reminders" },
      { p: "Compliance is a manual nightmare", a: "Compliance Agent attests + audits every clinical and billing action" },
    ],
    case: {
      headline: "Multi-site provider group · USA",
      pull: "Claims agent caught $4.2M in preventable denials in year one. First-pass approval rate jumped from 72% to 94%.",
      kpi: [{ k: "First-pass approval", v: "94%" }, { k: "Denials prevented", v: "$4.2M" }, { k: "Days to revenue", v: "−21" }],
    },
    agents: ["cx-concierge", "supplier-onboarding"],
  },
  {
    id: "hospitality", code: "HT", name: "Hospitality", sub: "Hotels · F&B · Travel",
    headline: "Every guest, remembered. Every room, optimized.",
    intro: "Concierge, revenue, housekeeping and F&B agents that work together. The result: higher RevPAR, faster service recovery, and loyalty value you can actually count.",
    pains: [
      { p: "Front desk drowns at peak, service recovery slips", a: "Concierge Agent resolves in-chat before a guest reaches the desk" },
      { p: "Revenue management is reactive", a: "RM Agent optimizes rates across channels every 15 min" },
      { p: "Housekeeping & maintenance run on radios", a: "Ops Agent dispatches, tracks SLAs, predicts equipment failures" },
      { p: "F&B forecasting is guesswork", a: "Forecast Agent reads bookings, weather, events to plan covers" },
    ],
    case: {
      headline: "Boutique hotel group · 22 properties",
      pull: "Concierge agent fixes guest problems before they finish typing. Repeat-stay rate up 11 points in 90 days.",
      kpi: [{ k: "RevPAR lift", v: "+11pts" }, { k: "CSAT", v: "4.8 / 5" }, { k: "Front-desk hours saved", v: "31k / yr" }],
    },
    agents: ["cx-concierge", "demand-forecast", "salesforce"],
  },
  {
    id: "film", code: "FM", name: "Film & Media", sub: "Production · Distribution · Audience",
    headline: "Compress production timelines. Read audiences in real time.",
    intro: "Production scheduling, rights intelligence, distribution analytics and audience modeling — agents that take administrative drag out of the creative process.",
    pains: [
      { p: "Production overruns blow budgets", a: "Schedule Agent re-plans in real time as locations and talent shift" },
      { p: "Rights tracking is in spreadsheets", a: "Rights Agent monitors clearances, expirations and royalties" },
      { p: "Audience signals come too late", a: "Audience Agent reads social + box-office signals hour by hour" },
      { p: "Distribution deals are opaque", a: "Distribution Agent benchmarks deals against comparable releases" },
    ],
    case: {
      headline: "Independent studio · 6 features / yr",
      pull: "Production overruns dropped 22% in one cycle. Schedule agent flagged risks 11 days earlier than human PMs.",
      kpi: [{ k: "Production overruns", v: "−22%" }, { k: "Schedule risk flagged", v: "11d earlier" }, { k: "Distribution yield", v: "+14%" }],
    },
    agents: ["cx-concierge", "demand-forecast"],
  },
  {
    id: "manufacturing", code: "MF", name: "Manufacturing & Industrial", sub: "Plant · Procurement · Logistics",
    headline: "Plants that adapt. Suppliers that talk. Logistics that don't surprise.",
    intro: "From plant operations to procurement to logistics — agents instrument the messy industrial reality you live in, and learn from every shift.",
    pains: [
      { p: "Procurement is fragmented and reactive", a: "Procurement Agent orchestrates RFQs + supplier comms end-to-end" },
      { p: "Quality drift gets caught at customer site", a: "Quality Agent reads sensor + vision data to catch drift early" },
      { p: "Logistics surprises blow OTIF", a: "Logistics Agent re-routes around real-world disruption" },
      { p: "Field service is a black box", a: "Service Agent dispatches techs with right-first-time parts" },
    ],
    case: {
      headline: "Industrial OEM · 11 plants",
      pull: "Procurement agent ran 9.2× the throughput of the prior team — and caught $3.1M in pricing anomalies in year one.",
      kpi: [{ k: "Procurement throughput", v: "9.2×" }, { k: "Pricing anomalies caught", v: "$3.1M" }, { k: "OTIF", v: "+18pts" }],
    },
    agents: ["supplier-onboarding", "inventory", "warehouse", "delivery"],
  },
];

const IndustryCard = ({ ind }) => (
  <section id={ind.id} className="scroll-mt-24 relative py-16 border-t border-white/[0.06]">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid items-start gap-10 lg:grid-cols-[1fr,1.05fr]">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-cyan-300/80">{ind.code} / vertical</span>
            <span className="h-px w-12 bg-gradient-to-r from-cyan-300/40 to-transparent" />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{ind.sub}</span>
          </div>
          <h2 className="mt-4 text-[36px] leading-[1.05] tracking-tight sm:text-[44px]"><span className="text-white">{ind.name}</span></h2>
          <p className="mt-3 font-display italic text-[22px] text-white/85">{ind.headline}</p>
          <p className="mt-5 text-[15px] leading-relaxed text-white/65">{ind.intro}</p>

          <div className="mt-7 space-y-2.5">
            {ind.pains.map((p,i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-white/[0.06] bg-white/[0.025] p-3.5">
                <span className="mt-[3px] grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-rose-400/15 text-rose-200">
                  <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </span>
                <div className="flex-1">
                  <div className="text-[13px] text-white/80">{p.p}</div>
                  <div className="text-[12.5px] text-cyan-200 mt-0.5"><span className="text-cyan-300">→</span> {p.a}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">Agents deployed</span>
            {ind.agents.map(aid => (
              <Link key={aid} to={`/agents#${aid}`} className="rounded border border-cyan-300/20 bg-cyan-300/[0.04] px-2 py-1 font-mono text-[10.5px] text-cyan-200 hover:bg-cyan-300/[0.1]">{aid}</Link>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <a href="/#book" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13.5px] font-semibold text-[#051030] hover:-translate-y-0.5">
              Build agents for our {ind.name.toLowerCase()} business
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#0b1535] to-[#070d24] p-7">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">Live customer</div>
          <h3 className="mt-2 text-[20px] font-semibold text-white">{ind.case.headline}</h3>
          <div className="mt-5 text-[40px] leading-none font-display text-cyan-300/30">&ldquo;</div>
          <p className="-mt-3 text-[16px] leading-relaxed text-white/80">{ind.case.pull}</p>
          <div className="mt-6 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.06]">
            {ind.case.kpi.map(k => (
              <div key={k.k} className="bg-[#070d24] p-4">
                <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-white/40">{k.k}</div>
                <div className="mt-1 text-[22px] font-semibold tracking-tight nums grad-text">{k.v}</div>
              </div>
            ))}
          </div>
          <a href="/#book" className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-cyan-300 hover:text-white">Talk to us about your numbers →</a>
        </div>
      </div>
    </div>
  </section>
);

const IndustriesHero = () => (
  <section className="relative overflow-hidden pt-32 pb-12 lg:pt-40 lg:pb-16">
    <div className="pointer-events-none absolute inset-0 bg-grid opacity-50" />
    <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[420px] w-[1000px] rounded-full blur-[120px] opacity-40"
         style={{ background: "radial-gradient(closest-side, rgba(76,125,255,0.45), rgba(94,234,212,0.15), transparent 70%)" }} />
    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <SectionLabel tone="accent">Industries</SectionLabel>
      <h1 className="mt-5 max-w-4xl text-[44px] leading-[1.02] tracking-tight sm:text-[58px] lg:text-[68px]">
        <span className="text-white">Verticalized depth.</span>{" "}
        <span className="font-display italic grad-text">Not generic AI.</span>
      </h1>
      <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/70">
        Each industry has its own operating model, regulations, vendors and rhythms. We co-design agents with operators who&rsquo;ve spent decades inside these businesses &mdash; so the agents reason like your best people, not like a chatbot.
      </p>
      <div className="mt-10 grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
        {INDUSTRIES.map(ind => (
          <a key={ind.id} href={`#${ind.id}`} className="group rounded-xl border border-white/[0.06] bg-white/[0.025] p-3 hover:border-cyan-300/30 hover:bg-white/[0.05]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300/70">{ind.code}</div>
            <div className="mt-1 text-[13px] font-semibold text-white">{ind.name}</div>
            <div className="text-[11px] text-white/45">{ind.sub}</div>
          </a>
        ))}
      </div>
    </div>
  </section>
);

function useHashScroll() {
  const loc = useLocation();
  useEffect(() => {
    if (!loc.hash) return;
    const el = document.getElementById(loc.hash.replace("#", ""));
    if (el) requestAnimationFrame(() => window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }));
  }, [loc.hash, loc.pathname]);
}

export default function IndustriesPage() {
  useHashScroll();
  return (
    <div className="ai-root min-h-screen" data-screen-label="EkkBaz AI · Industries">
      <Helmet>
        <title>Industries — EkkBaz AI | AI Agents for Consumer Goods, Healthcare, Manufacturing & More</title>
        <meta name="description" content="Industry-specific AI agents for Consumer Goods (FMCG), Electronics, Healthcare, Hospitality, Film & Media and Manufacturing — with verified KPIs from live deployments." />
        <link rel="canonical" href="https://ekkbaz.ai/industries" />
        <meta property="og:title" content="Industries — EkkBaz AI | Verticalized AI Agents" />
        <meta property="og:description" content="AI agents co-designed with operators across 6 industries. Consumer Goods, Electronics, Healthcare, Hospitality, Film & Media, Manufacturing." />
        <meta property="og:url" content="https://ekkbaz.ai/industries" />
        <meta property="og:image" content="https://ekkbaz.ai/assets/images/ekkbaz-og.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://ekkbaz.ai/industries",
          "name": "EkkBaz AI — Industries",
          "description": "Industry-specific AI agents for Consumer Goods, Electronics, Healthcare, Hospitality, Film & Media and Manufacturing.",
          "isPartOf": {"@id": "https://ekkbaz.ai/#website"}
        })}</script>
      </Helmet>
      <Nav />
      <main>
        <IndustriesHero />
        {INDUSTRIES.map(ind => <IndustryCard key={ind.id} ind={ind} />)}
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
